import { FC, useContext, useEffect, useState } from 'react';
import { IconCheck, IconPlug, IconRefreshAlert, IconSend } from '@tabler/icons';
import { Button, Center, Divider, Loader, Menu, Text, ThemeIcon, Title } from '@mantine/core';
import moment from 'moment';
import Moment from 'react-moment';
import { useNavigate } from 'react-router-dom';
import { closeAllModals, openModal } from '@mantine/modals';
import BookitupDocument from '../../Types/BookitupDocument';
import { useEmailAccountingConfig } from '../../Service/hooks/useEmailAccountingConfig';
import { EmailTransactionDTO, LexOfficeS } from '../../Service/LexOfficeS';
import { isDefined } from '../../Service/ValidationS';
import { DocumentContext } from '../../Service/Context/DocumentContextV2';
import { ThemeText } from '../../Atoms/ThemeText';

interface Props {
  bookingDocument: BookitupDocument;
  dropDownVesion?: boolean;
}

const EmailImportButton: FC<Props> = ({ bookingDocument, dropDownVesion }) => {
  const { handleChange } = useContext(DocumentContext);
  const { loading, config, email } = useEmailAccountingConfig();
  const [transmitting, setTransmitting] = useState(false);
  const [emailTransaction, setEmailTransaction] = useState<EmailTransactionDTO | null>();
  const navigate = useNavigate();
  const { id: documentId, lastModified } = bookingDocument;
  const { sentAt } = emailTransaction ?? { sentAt: null };
  const upToDate = sentAt !== null && moment(sentAt) > moment(lastModified);

  useEffect(() => {
    if (config) {
      LexOfficeS.getEmailTransaction(documentId).then(setEmailTransaction);
    }
  }, [config, documentId]);

  const getIcon = () => {
    if (emailTransaction) {
      if (upToDate) {
        return <IconCheck size={14} />;
      }
      return <IconRefreshAlert size={14} />;
    }
    return <IconPlug size={14} />;
  };

  const getDescription = () => {
    if (emailTransaction) {
      if (upToDate) {
        return (
          <>
            Rechnung übertragen <Moment date={sentAt} format="DD.MM.YY" />
          </>
        );
      }
      return <>Nicht auf dem neuesten Stand</>;
    }
    return (
      <Text span variant="link" onClick={() => navigate('/settings/bookkeeping')} sx={{ cursor: 'pointer' }}>
        Jetzt einrichten
      </Text>
    );
  };

  const transmit = async () => {
    if (!isDefined(email)) {
      const onClick = async () => {
        closeAllModals();
        navigate('/settings/bookkeeping');
      };
      openModal({
        title: <Title order={4}>Fehlende E-Mail-Adresse</Title>,
        children: (
          <>
            <Text>
              Du hast derzeit anscheinend keine E-Mail-Adresse für die Buchhaltung konfiguriert. Hinterlege bitte eine
              gültige E-Mail-Adresse, um diese Funktion nutzen zu können.
            </Text>
            <Center mt="xl">
              <Button onClick={onClick}>Jetzt einrichten</Button>
            </Center>
          </>
        ),
      });
    } else {
      setTransmitting(true);
      const ok = await LexOfficeS.transmitViaEmail(bookingDocument);
      setTransmitting(false);
      if (ok) {
        setEmailTransaction({ sentAt: new Date() });
        handleChange({ draft: false });
      }
    }
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {dropDownVesion ? (
        <Menu.Item icon={<IconSend size={16} />} onClick={transmit} disabled={upToDate || transmitting}>
          an Buchhaltung senden
        </Menu.Item>
      ) : (
        <div>
          <IconSend color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
          <Button
            leftIcon={transmitting && <Loader size="xs" />}
            onClick={transmit}
            disabled={upToDate || loading || transmitting}
          >
            an Buchhaltung senden
          </Button>
          {sentAt && (
            <ThemeText align="center" size="xs" mt="xs">
              <ThemeIcon radius="xl" size="sm" mr="sm">
                {getIcon()}
              </ThemeIcon>
              {getDescription()}
            </ThemeText>
          )}
          <Divider mt="sm" />
        </div>
      )}
    </>
  );
};

export default EmailImportButton;
